import * as React from 'react'
import Select from 'react-select'
import * as moment from 'moment'
import TimeOption from '../icons/ui/TimeOption'
import { ITripRequestObject } from 'src/types'
import UIMessages from 'src/utils/ui-messages'
import { Datepicker } from '@mobiscroll/react'
import { StylesConfig } from 'react-select/src/styles'
import { ControlProps } from 'react-select/src/components/Control'
import { SingleValueProps } from 'react-select/src/components/SingleValue'
import { MenuProps } from 'react-select/src/components/Menu'
import { IndicatorComponentType } from 'react-select/src/components'

interface IRequestTimeSettingsProps {
  request: ITripRequestObject
  scheduledPickupTime?: string
  scheduledDropoffTime?: string
  leaveAtDateTime?: string
  arriveByDateTime?: string
  setScheduledTime: (time: string, type: string) => Promise<any>
  refreshAvailableProducts: () => void
}

interface ITimeOption {
  label: string
  value: string
}

interface IRequestTimeSettingsState {
  selectedOption: ITimeOption
  hours: number
  minutes: number
  scheduledReturn: boolean
  returnPickupTime: string
}

const options = [
  { label: 'Now', value: 'now' },
  { label: 'Pickup time', value: 'pickup' },
  { label: 'Leave-at', value: 'leave-at' },
  { label: 'Arrive-by', value: 'arrive-by' },
  { label: 'Recurring Pickup Time', value: 'recurPickup' }
]
class RequestTimeSettings extends React.Component<IRequestTimeSettingsProps> {
  public state = {
    selectedOption: options[1],
    hours: 10,
    minutes: 30,
    scheduledReturn: false,
    returnPickupTime: ''
  }

  public componentDidMount() {
    const { request } = this.props

    const time = moment()

    this.props.setScheduledTime(time.toISOString(), 'pickup')

    if (request.recurringPickupTime) {
      const time = moment(request.recurringPickupTime)
      this.setState({
        selectedOption: options[4],
        hours: time.hours(),
        minutes: time.minutes()
      })
    }
  }

  public componentDidUpdate(prevProps: IRequestTimeSettingsProps, prevState: IRequestTimeSettingsState) {
    const { scheduledPickupTime, scheduledDropoffTime, leaveAtDateTime, arriveByDateTime } = this.props
    if (this.state.selectedOption.value !== prevState.selectedOption.value) {
      this.selectTimeOption(this.state.selectedOption)
      this.props.refreshAvailableProducts()
    }

    if (scheduledPickupTime && scheduledPickupTime !== prevProps.scheduledPickupTime) {
      this.setState({ selectedOption: options[1] })
    } else if (leaveAtDateTime && leaveAtDateTime !== prevProps.leaveAtDateTime) {
      this.setState({ selectedOption: options[2] })
    } else if (arriveByDateTime && arriveByDateTime !== prevProps.arriveByDateTime) {
      this.setState({ selectedOption: options[3] })
    } else if (scheduledDropoffTime && scheduledDropoffTime !== prevProps.scheduledDropoffTime) {
      this.setState({ selectedOption: options[4] })
    }
  }

  public componentWillUnmount() {
    this.props.setScheduledTime('', '')
  }

  public render() {
    const { scheduledPickupTime, scheduledDropoffTime, leaveAtDateTime, arriveByDateTime } = this.props
    let currentTime = moment()

    if (scheduledPickupTime) {
      currentTime = moment(scheduledPickupTime)
    } else if (leaveAtDateTime) {
      currentTime = moment(leaveAtDateTime)
    } else if (arriveByDateTime) {
      currentTime = moment(arriveByDateTime)
    } else if (scheduledDropoffTime) {
      currentTime = moment(scheduledDropoffTime)
    } else {
      currentTime = moment()
    }

    const FONT_SIZE = '14px'

    const styles: StylesConfig = {
      control: (provided: React.CSSProperties, state: ControlProps<ITimeOption>) => {
        return {
          cursor: 'pointer',
          display: 'flex',
          minHeight: '38px'
          // width: this.state.selectedOption.value === 'now' ? '5em' : '7em'
        }
      },
      singleValue: (provided: React.CSSProperties, state: SingleValueProps<ITimeOption>) => ({
        ...provided,
        color: '#137CBD',
        fontSize: FONT_SIZE
      }),
      menu: (provided: React.CSSProperties, state: MenuProps<ITimeOption>) => ({
        ...provided,
        textAlign: 'left',
        fontSize: FONT_SIZE
      }),
      dropdownIndicator: (provided: React.CSSProperties, state: IndicatorComponentType<ITimeOption>) => ({
        ...provided,
        color: '#137CBD'
      }),
      indicatorSeparator: (provided: React.CSSProperties, state: IndicatorComponentType<ITimeOption>) => ({
        width: 0
      })
    }

    const nowSelected = this.state.selectedOption.value === 'now'
    const recurringSelected = this.state.selectedOption.value === 'recurPickup'
    const selectStyles = nowSelected ? { width: '6em' } : recurringSelected ? { width: '27em' } : { width: '10em' }
    const { hours, minutes } = this.state
    const time = `${hours}:${minutes}`

    return (
      <div className='flex flex-column'>
        <div className='flex items-start'>
          <div>
            <TimeOption className='mt1 ml1' />
          </div>
          <div style={selectStyles}>
            <Select
              styles={styles}
              value={this.state.selectedOption}
              options={options}
              isSearchable={false}
              onChange={this.selectTimeOption}
            />
          </div>
          <div>
            {this.state.selectedOption.value !== 'now' ? (
              this.state.selectedOption.value === 'recurPickup' ||
              this.state.selectedOption.value === 'recurDropoff' ? (
                <Datepicker
                  controls={['time']}
                  defaultValue={time}
                  inputProps={{
                    className: 'border-none mt02 p1 rounded',
                    style: {
                      fontSize: FONT_SIZE,
                      outline: 'none',
                      width: 'calc(100% - 16px)'
                    }
                  }}
                  timeFormat='HH:mm'
                  onChange={({ value }) => this.handleTimeChanged(value)}
                />
              ) : (
                <Datepicker
                  controls={['calendar', 'time']}
                  dateFormat='MM/DD/YYYY'
                  inputProps={{
                    className: 'border-none mt02 p1 rounded',
                    style: {
                      fontSize: FONT_SIZE,
                      outline: 'none',
                      width: 'calc(100% - 16px)'
                    }
                  }}
                  min={moment()}
                  timeFormat='hh:mmA'
                  value={currentTime}
                  onChange={({ value }) => this.selectTime(value)}
                />
              )
            ) : null}
          </div>
        </div>
        <div className='flex flex-column items-start'>
          {this.state.selectedOption !== options[4] && (
            <div className='pl1 flex items-center font-grey2' style={{ whiteSpace: 'nowrap', fontSize: FONT_SIZE }}>
              <input type='checkbox' id='chkWillCall' onClick={e => this.setWillCall(e)} />{' '}
              {UIMessages.common.willCallLabel}
            </div>
          )}

          {this.state.selectedOption !== options[4] && (
            <div className='flex flex-column items-start'>
              <div className='pl1 flex items-center font-grey2' style={{ whiteSpace: 'nowrap', fontSize: FONT_SIZE }}>
                <input type='checkbox' id='chkScheduledReturn' onClick={e => this.setScheduledReturn(e)} />{' '}
                {UIMessages.common.scheduleReturnRide}
              </div>
              {this.state.scheduledReturn && (
                <div className='ml2 mt1 flex flex-column text-start'>
                  <label style={{ whiteSpace: 'nowrap', fontSize: FONT_SIZE }}>
                    {UIMessages.trip.returnPickUpTime}:{' '}
                  </label>
                  <Datepicker
                    controls={['calendar', 'time']}
                    dateFormat='MM/DD/YYYY'
                    inputProps={{
                      className: 'border-none mt02 rounded',
                      style: {
                        fontSize: FONT_SIZE,
                        outline: 'none',
                        width: 'auto'
                      }
                    }}
                    min={moment()}
                    timeFormat='hh:mmA'
                    value={this.state.returnPickupTime || moment()}
                    onChange={({ value }) => this.setScheduledReturnTime(value)}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }
  // date is added because it is needed in the back end when getting available trips,
  // during the scheduling of trips the date is ignored in backend
  private handleTimeChanged = (time: Date) => {
    const hours = Number(moment(time).format('HH'))
    const minutes = Number(moment(time).format('mm'))
    const recurringTime = moment() //.add(1, 'week') add might not be needed
    recurringTime.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
    const isoRecurringTime = recurringTime.toISOString()
    this.props.setScheduledTime(isoRecurringTime, this.state.selectedOption.value)
    this.setState({ hours, minutes })
  }

  private selectTimeOption = (value: any) => {
    if (value.value === this.state.selectedOption.value) {
      return
    }
    this.setState({ selectedOption: value })
    const time = moment()
    let currentTime = value.value === 'now' ? '' : time.toISOString()

    const { scheduledPickupTime, leaveAtDateTime, arriveByDateTime, scheduledDropoffTime } = this.props
    if (scheduledPickupTime) {
      currentTime = scheduledPickupTime
    } else if (leaveAtDateTime) {
      currentTime = leaveAtDateTime
    } else if (arriveByDateTime) {
      currentTime = arriveByDateTime
    } else if (scheduledDropoffTime) {
      currentTime = scheduledDropoffTime
    }
    this.props.setScheduledTime(currentTime, value.value)
    this.setState({ hours: time.hours(), minutes: time.minutes() })
  }

  private selectTime = (date: Date) => {
    const value = moment(date)
    //calculate the seconds
    const timeDiff = moment(value).unix() - moment().unix()
    if (moment.isMoment(value) && timeDiff >= 0) {
      this.props.setScheduledTime(value.utc().toISOString(), this.state.selectedOption.value)
    }
  }

  private setWillCall = (e: any) => {
    const wc = e.target.checked
    this.props.request.willCall = wc
  }

  private setScheduledReturn = (e: any) => {
    const sr = e.target.checked
    this.props.request.scheduledReturn = sr
    this.setState({ scheduledReturn: sr })
  }

  private setScheduledReturnTime = (date: Date) => {
    const value = moment(date)
    const timeDiff = moment(value).unix() - moment().unix()

    if (moment.isMoment(value) && timeDiff >= 0) {
      this.setState({ returnPickupTime: value })
      this.props.request.returnPickupTime = value.utc().toISOString()
    }
  }
}

export default RequestTimeSettings



// WEBPACK FOOTER //
// ./src/components/trips/RequestTimeSettings.tsx